import { useState, useEffect, useCallback } from "react";
import api from '../../lib/api';
import FormModal from '@shared/ui/FormModal';
import OrganizationLookupField from './OrganizationLookupField';
import { formatDistrict } from '../../utils/districtDisplay';
import { useOrgFilterOptions } from './useOrgFilterOptions';

interface CreateOrganizationModalProps {
  isOpen: boolean;
  onClose: () => void;
}

interface NewOrgForm {
  name: string;
  subscription: string;
  orgCategory: string;
  selectedLobbyist: string;
  buildWorkspaces: boolean;
  email: string;
  contact: string;
  website: string;
  phone: string;
  // Removed registrationCode
  userUsername: string;
  userEmail: string;
  userFirstName: string;
  userLastName: string;
  userDisplayName: string;
}

interface LobbyistOption {
  name: string;
  type: 'firm' | 'individual';
  displayName: string;
}

interface LookupResult {
  id: string;
  name: string;
  type: 'lobbyist' | 'legislator' | 'nonprofit';
  source_id: string;
  match_confidence: number;
  subtitle: string;
  status: string;
  data: {
    primary_name: string;
    suggested_org_category: string;
    [key: string]: any;
  };
}

export default function CreateOrganizationModal({
  isOpen,
  onClose,
}: CreateOrganizationModalProps) {
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [lobbyistOptions, setLobbyistOptions] = useState<LobbyistOption[]>([]);
  const [loadingLobbyists, setLoadingLobbyists] = useState(false);
  const [selectedLookupResult, setSelectedLookupResult] = useState<LookupResult | null>(null);
  const [isManualEntry, setIsManualEntry] = useState(false);

  // Cleanup preview URL on unmount
  useEffect(() => {
    return () => {
      if (previewUrl) {
        URL.revokeObjectURL(previewUrl);
      }
    };
  }, [previewUrl]);

  // Tiers from the subscription_types catalog — the same list Edit Organization shows.
  const tierOptions = useOrgFilterOptions({ enabled: isOpen }).data?.subscriptionTiers ?? [];

  const [newOrgForm, setNewOrgForm] = useState<NewOrgForm>({
    name: "",
    subscription: "none",
    orgCategory: "",
    selectedLobbyist: "",
    buildWorkspaces: false,
    email: "",
    contact: "",
    website: "",
    phone: "",
    // registrationCode removed
    userUsername: "",
    userEmail: "",
    userFirstName: "",
    userLastName: "",
    userDisplayName: "",
  });

  const resetForm = () => {
    setNewOrgForm({
      name: "",
      subscription: "none",
      orgCategory: "",
      selectedLobbyist: "",
      buildWorkspaces: false,
      email: "",
      contact: "",
      website: "",
      phone: "",
      // registrationCode removed
      userUsername: "",
      userEmail: "",
      userFirstName: "",
      userLastName: "",
      userDisplayName: "",
    });
    setSelectedFile(null);
    if (previewUrl) {
      URL.revokeObjectURL(previewUrl);
      setPreviewUrl(null);
    }
    setError(null);
    setLobbyistOptions([]);
    setSelectedLookupResult(null);
    setIsManualEntry(false);
  };

  // Fetch lobbyist options when Lobbyist/Lobbying Firm category is selected
  const fetchLobbyistOptions = async () => {
    try {
      setLoadingLobbyists(true);
      const response = await api.get('/lobbyists/organization-lookup/options');
      if (response.data.success) {
        setLobbyistOptions(response.data.data.options);
      }
    } catch (error) {
      console.error('Error fetching lobbyist options:', error);
      setError('Failed to load lobbyist options');
    } finally {
      setLoadingLobbyists(false);
    }
  };

  // Populate form from lookup result
  const populateFormFromLookup = useCallback((result: LookupResult) => {
    const { data } = result;
    
    // Build organization name based on type
    let orgName = data.primary_name;
    if (result.type === 'legislator') {
      orgName = `${data.primary_name} - ${formatDistrict(null, String(data.district ?? ''), data.chamber)}`;
    }
    
    // Set form data
    const updatedForm = {
      ...newOrgForm,
      name: orgName,
      orgCategory: data.suggested_org_category,
      email: data.email || newOrgForm.email,
      phone: data.phone || newOrgForm.phone,
      website: data.website || newOrgForm.website,
      contact: data.individual_name || data.primary_name || newOrgForm.contact
    };
    
    // Handle lobbyist-specific data
    if (result.type === 'lobbyist' && data.suggested_org_category === 'Lobbyist/Lobbying Firm') {
      updatedForm.selectedLobbyist = data.primary_name;
      fetchLobbyistOptions();
    }
    
    setNewOrgForm(updatedForm);
    setSelectedLookupResult(result);
    setIsManualEntry(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  
  // Clear lookup and enable manual entry
  const clearLookupAndGoManual = () => {
    setSelectedLookupResult(null);
    setIsManualEntry(true);
    setNewOrgForm({
      name: "",
      subscription: "none",
      orgCategory: "",
      selectedLobbyist: "",
      buildWorkspaces: false,
      email: "",
      contact: "",
      website: "",
      phone: "",
      userUsername: "",
      userEmail: "",
      userFirstName: "",
      userLastName: "",
      userDisplayName: "",
    });
  };

  // Handle category change
  const handleCategoryChange = (category: string) => {
    setNewOrgForm({ 
      ...newOrgForm, 
      orgCategory: category,
      selectedLobbyist: "",
      buildWorkspaces: false, // Reset checkbox when category changes
      name: selectedLookupResult ? newOrgForm.name : "" // Keep name if from lookup
    });
    
    if (category === "Lobbyist/Lobbying Firm") {
      fetchLobbyistOptions();
    } else {
      setLobbyistOptions([]);
    }
  };

  // Handle lobbyist selection
  const handleLobbyistSelect = (lobbyistName: string) => {
    setNewOrgForm({
      ...newOrgForm,
      selectedLobbyist: lobbyistName,
      name: lobbyistName // Auto-fill organization name with selected lobbyist
    });
  };

  const handleCreateOrg = async () => {
    // Category validation
    if (!newOrgForm.orgCategory.trim()) {
      setError("Organization category is required");
      return;
    }

    // Lobbyist validation for Lobbyist/Lobbying Firm category (only if not manual entry)
    if (newOrgForm.orgCategory === "Lobbyist/Lobbying Firm" && !isManualEntry && !newOrgForm.selectedLobbyist.trim()) {
      setError("Please select a lobbyist from the directory or enable manual entry");
      return;
    }

    // Organization validation
    if (!newOrgForm.name.trim()) {
      setError("Organization name is required");
      return;
    }

    // Removed registrationCode validation

    // User validation
    if (!newOrgForm.userUsername.trim()) {
      setError("Admin username is required");
      return;
    }

    if (!newOrgForm.userEmail.trim()) {
      setError("Admin email is required");
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      // Prepare organization data with lookup source information
      const orgData = {
        ...newOrgForm,
        // Include lookup source information if available
        lookupSource: selectedLookupResult ? {
          type: selectedLookupResult.type,
          sourceId: selectedLookupResult.source_id,
          referenceId: selectedLookupResult.id
        } : null
      };

      // First create the organization
      const response = await api.post("/admin/organizations", orgData);
      const createdOrg = response.data;

      // If there's a logo file, upload it
      if (selectedFile && createdOrg?.organization?.id) {
        const formData = new FormData();
        formData.append("logo", selectedFile);

        try {
          await api.put(`/admin/organizations/${createdOrg.organization.id}/upload-logo`, formData, {
            headers: {
              'Content-Type': 'multipart/form-data',
            },
          });
        } catch (logoError) {
          console.warn("Logo upload failed, but organization was created successfully");
        }
      }

      // Success
      if (response.data.wasUpgrade) {
        alert("Existing organization upgraded with user access successfully!");
      } else {
        alert("Organization and admin user created successfully");
      }
      resetForm();
      onClose();
    } catch (error: any) {
      setError(error.response?.data?.error || error.message || "Failed to create organization");
    } finally {
      setIsLoading(false);
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      // Validate file type
      const validTypes = ["image/jpeg", "image/jpg", "image/png", "image/svg+xml", "image/webp"];
      if (!validTypes.includes(file.type)) {
        setError("Please select a PNG, JPG, JPEG, SVG, or WebP image");
        return;
      }
      
      // Validate file size (5MB limit)
      if (file.size > 5 * 1024 * 1024) {
        setError("Please select an image smaller than 5MB");
        return;
      }
      
      setSelectedFile(file);
      setError(null);
      
      // Create preview URL
      const url = URL.createObjectURL(file);
      setPreviewUrl(url);
    }
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  if (!isOpen) return null;

  return (
    <FormModal
      isOpen={isOpen}
      title="Create organization"
      onClose={handleClose}
      onSubmit={handleCreateOrg}
      size="xl"
      submitLabel="Create organization"
      isSubmitting={isLoading}
    >
      <div className="space-y-4 sm:space-y-6">
            {error && (
              <div className="mb-4 p-3 border border-error-300 bg-error-50 text-error-700 dark:border-error-800 dark:bg-error-900/20 dark:text-error-300 rounded-lg">
                {error}
              </div>
            )}
          {/* Organization Lookup */}
          {!isManualEntry && (
            <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-4 sm:p-6 border border-blue-200 dark:border-blue-800">
              <h3 className="text-base sm:text-lg font-semibold mb-3 sm:mb-4 text-blue-900 dark:text-blue-300">Find Existing Organization</h3>
              <p className="text-xs sm:text-sm text-blue-700 dark:text-blue-400 mb-4">
                Search our database of lobbyists, legislators, and non-profit organizations to automatically populate organization details.
              </p>
              <OrganizationLookupField
                onSelect={populateFormFromLookup}
                onClear={clearLookupAndGoManual}
                selectedResult={selectedLookupResult}
                placeholder="Search by name, organization, or EIN..."
                sources={['lobbyist', 'legislator', 'nonprofit']}
              />
              {!selectedLookupResult && (
                <div className="mt-4">
                  <button
                    type="button"
                    onClick={() => setIsManualEntry(true)}
                    className="text-sm text-blue-600 hover:text-blue-800 underline"
                  >
                    Skip lookup and enter details manually
                  </button>
                </div>
              )}
            </div>
          )}

          {/* Organization Details */}
          <div className="bg-background-tertiary dark:bg-background-dark-tertiary rounded-lg p-4 sm:p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-base sm:text-lg font-semibold text-text-primary dark:text-text-dark-primary">Organization Details</h3>
              {selectedLookupResult && (
                <button
                  type="button"
                  onClick={clearLookupAndGoManual}
                  className="text-sm text-text-secondary dark:text-text-dark-secondary hover:text-text-primary dark:hover:text-text-dark-primary underline"
                >
                  Clear and edit manually
                </button>
              )}
            </div>
            {selectedLookupResult && (
              <div className="mb-4 p-3 bg-green-100 border border-green-200 rounded-lg">
                <p className="text-sm text-green-800">
                  <strong>Populated from:</strong> {selectedLookupResult.name} ({selectedLookupResult.type})
                </p>
              </div>
            )}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-text-primary dark:text-text-dark-primary mb-1">
                  Organization Category *
                </label>
                <select
                  value={newOrgForm.orgCategory}
                  onChange={(e) => handleCategoryChange(e.target.value)}
                  className="input"
                >
                  <option value="">Select category first</option>
                  <option value="Association">Association</option>
                  <option value="Non-profit">Non-profit</option>
                  <option value="Business">Business</option>
                  <option value="Advocacy Group">Advocacy Group</option>
                  <option value="Political Candidate/Politician">Political Candidate/Politician</option>
                  <option value="PAC">PAC</option>
                  <option value="Media Organization">Media Organization</option>
                  <option value="Lobbyist/Lobbying Firm">Lobbyist/Lobbying Firm</option>
                  <option value="Government Agency">Government Agency</option>
                  <option value="Educational Institution">Educational Institution</option>
                  <option value="Other">Other</option>
                </select>
              </div>

              {/* Lobbyist Selection (only for Lobbyist/Lobbying Firm category) */}
              {newOrgForm.orgCategory === "Lobbyist/Lobbying Firm" && (
                <>
                  {/* Manual Entry Toggle */}
                  <div className="md:col-span-2">
                    <label className="flex items-center space-x-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={isManualEntry}
                        onChange={(e) => {
                          setIsManualEntry(e.target.checked);
                          if (e.target.checked) {
                            // Clear lobbyist selection when switching to manual entry
                            setNewOrgForm({ ...newOrgForm, selectedLobbyist: "" });
                            setSelectedLookupResult(null);
                          }
                        }}
                        className="w-4 h-4 text-primary-600 border-border-light dark:border-border-dark rounded focus:ring-primary-500"
                      />
                      <span className="text-sm font-medium text-text-primary dark:text-text-dark-primary">
                        Create organization not in directory (manual entry)
                      </span>
                    </label>
                    <p className="text-xs text-text-tertiary dark:text-text-dark-tertiary mt-1 ml-6">
                      Check this box if the lobbyist/firm doesn&apos;t exist in the Colorado Lobbyist Directory
                    </p>
                  </div>

                  {/* Lobbyist Directory Selection (hidden when manual entry is enabled) */}
                  {!isManualEntry && (
                    <div className="md:col-span-2">
                      <label className="block text-sm font-medium text-text-primary dark:text-text-dark-primary mb-1">
                        Select Lobbyist from Directory *
                      </label>
                      {loadingLobbyists ? (
                        <div className="w-full px-3 py-2 border border-border-light dark:border-border-dark rounded-lg bg-background-tertiary dark:bg-background-dark-tertiary">
                          Loading lobbyist options...
                        </div>
                      ) : (
                        <select
                          value={newOrgForm.selectedLobbyist}
                          onChange={(e) => handleLobbyistSelect(e.target.value)}
                          className="input"
                        >
                          <option value="">Select a lobbyist</option>
                          {lobbyistOptions.map((option, index) => (
                            <option key={index} value={option.name}>
                              {option.displayName}
                            </option>
                          ))}
                        </select>
                      )}
                      <p className="text-xs text-text-tertiary dark:text-text-dark-tertiary mt-1">
                        Select an existing lobbyist from the Colorado Lobbyist Directory
                      </p>
                    </div>
                  )}
                </>
              )}

              <div>
                <label className="block text-sm font-medium text-text-primary dark:text-text-dark-primary mb-1">
                  Organization Name *
                </label>
                <input
                  type="text"
                  placeholder={
                    selectedLookupResult 
                      ? "Name populated from lookup result" 
                      : newOrgForm.orgCategory === "Lobbyist/Lobbying Firm" && !isManualEntry
                        ? "Select lobbyist first or enable manual entry" 
                        : "Organization name"
                  }
                  value={newOrgForm.name}
                  onChange={(e) =>
                    setNewOrgForm({ ...newOrgForm, name: e.target.value })
                  }
                  disabled={
                    (!newOrgForm.orgCategory || (newOrgForm.orgCategory === "Lobbyist/Lobbying Firm" && !newOrgForm.selectedLobbyist && !selectedLookupResult && !isManualEntry))
                  }
                  className={`w-full px-3 py-2 border border-border-light dark:border-border-dark rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 ${
                    (!newOrgForm.orgCategory || (newOrgForm.orgCategory === "Lobbyist/Lobbying Firm" && !newOrgForm.selectedLobbyist && !selectedLookupResult && !isManualEntry)) 
                      ? 'bg-background-tertiary dark:bg-background-dark-tertiary cursor-not-allowed' 
                      : selectedLookupResult
                        ? 'bg-green-50 border-green-300'
                        : ''
                  }`}
                />
                {!newOrgForm.orgCategory && (
                  <p className="text-xs text-text-tertiary dark:text-text-dark-tertiary mt-1">Please select a category first</p>
                )}
                {newOrgForm.orgCategory === "Lobbyist/Lobbying Firm" && !newOrgForm.selectedLobbyist && !isManualEntry && (
                  <p className="text-xs text-text-tertiary dark:text-text-dark-tertiary mt-1">Please select a lobbyist or enable manual entry</p>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-text-primary dark:text-text-dark-primary mb-1">
                  Subscription *
                </label>
                <select
                  value={newOrgForm.subscription}
                  onChange={(e) =>
                    setNewOrgForm({ ...newOrgForm, subscription: e.target.value })
                  }
                  className="input"
                >
                  {/* Same catalog list as Edit Organization; 'none' stays the default */}
                  {!tierOptions.some((tier) => tier.key === newOrgForm.subscription) && (
                    <option value={newOrgForm.subscription}>{newOrgForm.subscription}</option>
                  )}
                  {tierOptions.map((tier) => (
                    <option key={tier.key} value={tier.key}>
                      {tier.label}
                    </option>
                  ))}
                </select>
              </div>
              {/* Removed Registration Code field */}
              <div>
                <label className="block text-sm font-medium text-text-primary dark:text-text-dark-primary mb-1">
                  Email
                </label>
                <input
                  type="email"
                  placeholder={selectedLookupResult ? "Email populated from lookup" : "Organization email"}
                  value={newOrgForm.email}
                  onChange={(e) =>
                    setNewOrgForm({ ...newOrgForm, email: e.target.value })
                  }
                  className={`w-full px-3 py-2 border border-border-light dark:border-border-dark rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 ${
                    selectedLookupResult && newOrgForm.email ? 'bg-green-50 border-green-300' : ''
                  }`}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-text-primary dark:text-text-dark-primary mb-1">
                  Phone
                </label>
                <input
                  type="tel"
                  placeholder={selectedLookupResult ? "Phone populated from lookup" : "Organization phone"}
                  value={newOrgForm.phone}
                  onChange={(e) =>
                    setNewOrgForm({ ...newOrgForm, phone: e.target.value })
                  }
                  className={`w-full px-3 py-2 border border-border-light dark:border-border-dark rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 ${
                    selectedLookupResult && newOrgForm.phone ? 'bg-green-50 border-green-300' : ''
                  }`}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-text-primary dark:text-text-dark-primary mb-1">
                  Contact Person
                </label>
                <input
                  type="text"
                  placeholder={selectedLookupResult ? "Contact populated from lookup" : "Primary contact name"}
                  value={newOrgForm.contact}
                  onChange={(e) =>
                    setNewOrgForm({ ...newOrgForm, contact: e.target.value })
                  }
                  className={`w-full px-3 py-2 border border-border-light dark:border-border-dark rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 ${
                    selectedLookupResult && newOrgForm.contact ? 'bg-green-50 border-green-300' : ''
                  }`}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-text-primary dark:text-text-dark-primary mb-1">
                  Website
                </label>
                <input
                  type="url"
                  placeholder={selectedLookupResult ? "Website populated from lookup" : "Organization website"}
                  value={newOrgForm.website}
                  onChange={(e) =>
                    setNewOrgForm({ ...newOrgForm, website: e.target.value })
                  }
                  className={`w-full px-3 py-2 border border-border-light dark:border-border-dark rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 ${
                    selectedLookupResult && newOrgForm.website ? 'bg-green-50 border-green-300' : ''
                  }`}
                />
              </div>
            </div>

            <div className="mt-4">
              <label className="block text-sm font-medium text-text-primary dark:text-text-dark-primary mb-1">
                Organization Logo
              </label>
              <div className="space-y-3">
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleFileChange}
                  className="w-full px-3 py-2 border border-border-light dark:border-border-dark rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 cursor-pointer"
                />
                {selectedFile && previewUrl && (
                  <div className="flex items-center space-x-4 p-3 border border-border-light rounded-lg bg-background-secondary">
                    <div className="w-16 h-16 bg-white rounded-md overflow-hidden border">
                      <img 
                        src={previewUrl} 
                        alt="Logo preview" 
                        className="w-full h-full object-cover"
                      />
                    </div>
                    <div className="flex-1">
                      <p className="text-sm font-medium text-text-primary dark:text-text-dark-primary">
                        {selectedFile.name}
                      </p>
                      <p className="text-xs text-neutral-500">
                        {(selectedFile.size / 1024 / 1024).toFixed(2)} MB
                      </p>
                    </div>
                    <button
                      type="button"
                      onClick={() => {
                        setSelectedFile(null);
                        if (previewUrl) {
                          URL.revokeObjectURL(previewUrl);
                          setPreviewUrl(null);
                        }
                      }}
                      className="px-3 py-1 text-sm border border-border-light dark:border-border-dark rounded-md hover:bg-background-tertiary"
                    >
                      Remove
                    </button>
                  </div>
                )}
                <p className="text-xs text-neutral-500">
                  Upload PNG, JPG, JPEG, SVG, or WebP files. Maximum size: 5MB.
                </p>
              </div>
            </div>
          </div>

          {/* Organization Administrator */}
          <div className="bg-white dark:bg-background-dark-secondary/50 rounded-lg p-4 sm:p-6">
            <h3 className="text-base sm:text-lg font-semibold mb-4 text-text-primary dark:text-text-dark-primary">Organization Administrator</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-text-primary dark:text-text-dark-primary mb-1">
                  Username *
                  <span className="text-xs text-text-tertiary dark:text-text-dark-tertiary ml-2">
                    (letters, numbers, underscore only)
                  </span>
                </label>
                <input
                  type="text"
                  placeholder="username (no spaces or special characters)"
                  value={newOrgForm.userUsername}
                  onChange={(e) => {
                    // Auto-sanitize username: remove spaces and special chars except underscore
                    const sanitized = e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, '');
                    setNewOrgForm({ ...newOrgForm, userUsername: sanitized });
                  }}
                  className="input"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-text-primary dark:text-text-dark-primary mb-1">
                  Email *
                </label>
                <input
                  type="email"
                  placeholder="Admin email address"
                  value={newOrgForm.userEmail}
                  onChange={(e) =>
                    setNewOrgForm({ ...newOrgForm, userEmail: e.target.value })
                  }
                  className="input"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-text-primary dark:text-text-dark-primary mb-1">
                  First Name
                </label>
                <input
                  type="text"
                  placeholder="First name"
                  value={newOrgForm.userFirstName}
                  onChange={(e) =>
                    setNewOrgForm({ ...newOrgForm, userFirstName: e.target.value })
                  }
                  className="input"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-text-primary dark:text-text-dark-primary mb-1">
                  Last Name
                </label>
                <input
                  type="text"
                  placeholder="Last name"
                  value={newOrgForm.userLastName}
                  onChange={(e) =>
                    setNewOrgForm({ ...newOrgForm, userLastName: e.target.value })
                  }
                  className="input"
                />
              </div>
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-text-primary dark:text-text-dark-primary mb-1">
                  Display Name
                </label>
                <input
                  type="text"
                  placeholder="Admin display name"
                  value={newOrgForm.userDisplayName}
                  onChange={(e) =>
                    setNewOrgForm({ ...newOrgForm, userDisplayName: e.target.value })
                  }
                  className="input"
                />
              </div>
            </div>

            <div className="mt-4 p-3 bg-blue-50 rounded-lg">
              <p className="text-sm text-blue-700">
                Default password will be &quot;admin123&quot;
              </p>
            </div>
          </div>

          {/* Build Workspaces Option (only for Lobbyist/Lobbying Firm) */}
          {newOrgForm.orgCategory === "Lobbyist/Lobbying Firm" && newOrgForm.selectedLobbyist && (
            <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-4 sm:p-6 border border-blue-200 dark:border-blue-800">
              <div className="flex items-start space-x-3">
                <input
                  type="checkbox"
                  id="buildWorkspaces"
                  checked={newOrgForm.buildWorkspaces}
                  onChange={(e) =>
                    setNewOrgForm({ ...newOrgForm, buildWorkspaces: e.target.checked })
                  }
                  className="mt-1 h-4 w-4 text-primary-600 focus:ring-primary-500 border-border-light dark:border-border-dark rounded"
                />
                <div className="flex-1">
                  <label htmlFor="buildWorkspaces" className="block text-sm font-medium text-blue-900 mb-1">
                    Build Workspaces Automatically
                  </label>
                  <p className="text-sm text-blue-700">
                    Automatically create client workspaces and add bill tracking based on the selected lobbyist&apos;s data. 
                    A workspace will be created for each unique client, with all associated bills automatically tracked.
                  </p>
                  {loadingLobbyists && (
                    <p className="text-xs text-blue-600 mt-1 italic">
                      Loading lobbyist data to determine available workspaces...
                    </p>
                  )}
                </div>
              </div>
            </div>
          )}
      </div>
    </FormModal>
  );
}
